import React, { useMemo, useState, useContext } from 'react'
import { graphql, useFragment } from 'react-relay'
import Animated, {
  withSpring,
  useSharedValue,
  useAnimatedStyle,
} from 'react-native-reanimated'

import { lazy as loadable } from '@loadable/component'

import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarYears,
  getHours,
} from 'date-fns'
import { format, utcToZonedTime, getTimezoneOffset } from 'date-fns-tz'
import enUS from 'date-fns/locale/en-US'
import enGB from 'date-fns/locale/en-GB'

import { YStack, XStack, Tooltip, StackProps, isWeb } from 'tamagui'
import {
  Paragraph,
  Icon,
  Text,
  Dialog,
  wrapSuspenseComponent,
} from '@newComponents'

import { ServerContext } from '@data/ServerContext'

import {
  EventScreenDateTime_event$key,
  EventScreenDateTime_event$data,
} from './__generated__/EventScreenDateTime_event.graphql'

const EventScreenAddToCalendarDialog = wrapSuspenseComponent(
  loadable(() => import('./EventScreenAddToCalendarDialog'))
)

const AnimatedYStack = Animated.createAnimatedComponent(YStack)

export const EventScreenDateTime = ({
  event,
}: {
  event: EventScreenDateTime_event$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenDateTime_event on Event {
        slug

        startAt
        endAt

        timeZone

        hasVenue
        hasExternalUrl

        myAttendance {
          status
        }
      }
    `,
    event
  )

  const [displayEventTimeZone, setDisplayEventTimeZone] = useState(
    data.myAttendance?.status === 'ATTENDING_IN_PERSON' ||
      (data.hasVenue && !data.hasExternalUrl)
      ? true
      : false
  )

  const serverContext = useContext(ServerContext)

  const displayTimeZone = useMemo(() => {
    try {
      if (!displayEventTimeZone) {
        const userTimeZone =
          serverContext?.timeZone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone

        if (userTimeZone && userTimeZone !== 'Etc/Unknown') {
          return userTimeZone
        }
      }
    } catch (err) {}

    return data.timeZone
  }, [data, displayEventTimeZone, serverContext])

  const startDate = useMemo(() => {
    return utcToZonedTime(new Date(data.startAt), displayTimeZone)
  }, [data, displayTimeZone])

  const endDate = useMemo(() => {
    return utcToZonedTime(new Date(data.endAt), displayTimeZone)
  }, [data, displayTimeZone])

  const formatOptions = useMemo(() => {
    const timeZoneOffset = getTimezoneOffset(
      displayTimeZone,
      new Date(data.startAt)
    )

    const locale = timeZoneOffset < -2 * 60 * 60 * 1000 ? enUS : enGB

    return { locale, timeZone: displayTimeZone }
  }, [data, displayTimeZone])

  return (
    <XStack gap='$4' alignItems='center' cursor='pointer' userSelect='none'>
      <AddToCalendarDialog data={data}>
        <CalendarIcon startDate={startDate} formatOptions={formatOptions} />
      </AddToCalendarDialog>

      <Tooltip placement='top' delay={0}>
        <Tooltip.Trigger asChild>
          <YStack
            tag='dialog'
            gap='$1'
            onPress={() => {
              setDisplayEventTimeZone((value) => !value)
            }}
          >
            <DateTime
              startDate={startDate}
              endDate={endDate}
              formatOptions={formatOptions}
            />
          </YStack>
        </Tooltip.Trigger>

        <Tooltip.Content
          animation='fastSpring'
          enterStyle={{ x: 0, y: 5, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 5, opacity: 0, scale: 0.9 }}
          scale={1}
          x={0}
          y={0}
          opacity={1}
          borderColor='rgba(105, 102, 118, 1.0)'
          backgroundColor='rgba(105, 102, 118, 0.8)'
          style={{
            backdropFilter: 'blur(8px) saturate(250%)',
            WebkitBackdropFilter: 'blur(8px) saturate(250%)',
          }}
        >
          <Tooltip.Arrow
            borderColor='rgba(105, 102, 118, 1.0)'
            backgroundColor='rgba(105, 102, 118, 0.7)'
            style={{
              backdropFilter: 'blur(8px) saturate(250%)',
              WebkitBackdropFilter: 'blur(8px) saturate(250%)',
            }}
          />

          <Paragraph size='$4' color='$grey0' fontWeight='$5'>
            {displayEventTimeZone ? 'Event Time' : 'Your Local Time'}
          </Paragraph>
        </Tooltip.Content>
      </Tooltip>
    </XStack>
  )
}

const CalendarIcon = ({
  startDate,
  formatOptions,
  ...props
}: {
  startDate: Date
  formatOptions: { locale: typeof enUS | typeof enGB; timeZone: string }
} & StackProps) => {
  return (
    <YStack
      {...props}
      width='$10'
      height='$10'
      alignItems='center'
      borderRadius='$2'
      // @ts-ignore boxShadow is web-only
      boxShadow='$boxShadowLight'
    >
      <Paragraph
        size='$2'
        fontWeight='$6'
        textTransform='uppercase'
        backgroundColor='$grey4'
        width='100%'
        textAlign='center'
        paddingVertical={2}
        borderTopLeftRadius='$2'
        borderTopRightRadius='$2'
        marginHorizontal={-1}
      >
        {format(startDate, 'MMM', formatOptions)}
      </Paragraph>

      <YStack
        flex={1}
        alignItems='center'
        justifyContent='center'
        borderWidth={1}
        borderTopWidth={0}
        borderColor='$grey4'
        borderBottomLeftRadius='$2'
        borderBottomRightRadius='$2'
        width='100%'
        backgroundColor='$grey0'
      >
        <Paragraph
          fontWeight='$5'
          // letterSpacing={1}
          textAlign='center'
          // paddingLeft={1}
        >
          {format(startDate, 'd', formatOptions)}
        </Paragraph>
      </YStack>
    </YStack>
  )
}

const AddToCalendarDialog = ({
  children,
  data,
}: {
  children: React.ReactNode
  data: EventScreenDateTime_event$data
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const calendarBlur = useSharedValue(0)

  const animatedBackgroundStyle = useAnimatedStyle(() => {
    return {
      filter: `blur(${calendarBlur.value}px)`,
    }
  }, [isHovered])

  return (
    <Tooltip
      placement='top'
      delay={100}
      onOpenChange={(open) => {
        if (open) {
          setIsHovered(true)
          calendarBlur.value = withSpring(2, {
            damping: 50,
            mass: 2,
            stiffness: 500,
          })
        } else {
          setIsHovered(false)

          calendarBlur.value = withSpring(0, {
            damping: 50,
            mass: 2,
            stiffness: 500,
          })
        }
      }}
    >
      <Tooltip.Trigger tag='dialog'>
        <Dialog modal>
          <Dialog.Trigger tag='dialog'>
            <YStack>
              <AnimatedYStack style={animatedBackgroundStyle}>
                {children}
              </AnimatedYStack>

              <YStack
                width='$10'
                height='$10'
                borderRadius='$1'
                position='absolute'
                alignItems='center'
                justifyContent='center'
                overflow='hidden'
              >
                <YStack
                  animation='fastSpring'
                  backgroundColor='$darkBlue10'
                  padding='$1'
                  // @ts-expect-error - borderRadius '50%' is a web-only value
                  borderRadius='50%'
                  // transform={[{ scale: isHovered ? 1 : 0 }]}
                  // transform={[{ translateX: isHovered ? 0 : -40 }]}
                  transform={[{ translateY: isHovered ? 0 : 40 }]}
                >
                  <Icon name='add-filled' color='$grey0' size='$5' />
                </YStack>
              </YStack>
            </YStack>
          </Dialog.Trigger>

          <Dialog.Adapt when='xs'>
            <Dialog.Sheet key='sheet'>
              <Dialog.Sheet.Handle zIndex={200_000} />
              <Dialog.Sheet.Frame
                padding={0}
                gap='$4'
                shadowColor='$grey6'
                shadowRadius='$2'
                borderTopRightRadius='$6'
                borderTopLeftRadius='$6'
              >
                <Dialog.Sheet.ScrollView
                  // borderTopRightRadius='$6'
                  // borderTopLeftRadius='$6'
                  padding={0}
                  contentContainerStyle={{ flex: 1 }}
                >
                  {isWeb ? (
                    <EventScreenAddToCalendarDialog eventSlug={data.slug} />
                  ) : (
                    <Dialog.Adapt.Contents />
                  )}
                </Dialog.Sheet.ScrollView>
              </Dialog.Sheet.Frame>
            </Dialog.Sheet>
          </Dialog.Adapt>

          <Dialog.Portal>
            <Dialog.Content
              elevate
              key='content'
              gap='$4'
              minWidth={400}
              maxWidth={600}
              minHeight={400}
              maxHeight={600}
              padding={0}
            >
              <EventScreenAddToCalendarDialog eventSlug={data.slug} />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog>
      </Tooltip.Trigger>

      <Tooltip.Content
        animation='fastSpring'
        enterStyle={{ x: 0, y: 5, opacity: 0, scale: 0.9 }}
        exitStyle={{ x: 0, y: 5, opacity: 0, scale: 0.9 }}
        scale={1}
        x={0}
        y={0}
        opacity={1}
        borderColor='rgba(105, 102, 118, 1.0)'
        backgroundColor='rgba(105, 102, 118, 0.8)'
        style={{
          backdropFilter: 'blur(8px) saturate(250%)',
          WebkitBackdropFilter: 'blur(8px) saturate(250%)',
        }}
      >
        <Tooltip.Arrow
          borderColor='rgba(105, 102, 118, 1.0)'
          backgroundColor='rgba(105, 102, 118, 0.7)'
          style={{
            backdropFilter: 'blur(8px) saturate(250%)',
            WebkitBackdropFilter: 'blur(8px) saturate(250%)',
          }}
        />

        <Paragraph size='$4' color='$grey0' fontWeight='$5'>
          Add to Calendar
        </Paragraph>
      </Tooltip.Content>
    </Tooltip>
  )
}

const DateTime = ({
  startDate,
  endDate,
  formatOptions,
}: {
  startDate: Date
  endDate: Date
  formatOptions: { locale: typeof enUS | typeof enGB; timeZone: string }
}) => {
  const startEndDiff = useMemo(() => {
    return differenceInCalendarDays(endDate, startDate)
  }, [startDate, endDate])

  if (startEndDiff === 0 || (startEndDiff === 1 && getHours(endDate) < 10)) {
    return (
      <SameDay
        startDate={startDate}
        endDate={endDate}
        formatOptions={formatOptions}
      />
    )
  }

  return (
    <DifferentDays
      startDate={startDate}
      endDate={endDate}
      formatOptions={formatOptions}
    />
  )
}

const SameDay = ({
  startDate,
  endDate,
  formatOptions,
}: {
  startDate: Date
  endDate: Date
  formatOptions: { locale: typeof enUS | typeof enGB; timeZone: string }
}) => {
  const dayDiff = useMemo(() => {
    return differenceInCalendarDays(startDate, new Date())
  }, [startDate])

  const yearDiff = useMemo(() => {
    return differenceInCalendarYears(startDate, new Date())
  }, [startDate])

  const timeDisplay = useMemo(() => {
    return (
      <Paragraph size='$4' fontWeight='$5' color='$grey8'>
        {format(startDate, `h:mma`, formatOptions)}{' '}
        <Text size='$4' color='$grey7' fontWeight='$3'>
          to
        </Text>{' '}
        {format(endDate, `h:mma`, formatOptions)}{' '}
        <Text size='$4' color='$grey7' fontWeight='$3'>
          {format(startDate, 'zzz', formatOptions)}{' '}
        </Text>
      </Paragraph>
    )
  }, [startDate, endDate, formatOptions])

  if (dayDiff === 0) {
    return (
      <>
        <Paragraph size='$5' fontWeight='$5'>
          Today
        </Paragraph>

        {timeDisplay}
      </>
    )
  }

  if (dayDiff === 1) {
    return (
      <>
        <Paragraph size='$5' fontWeight='$5'>
          Tomorrow
        </Paragraph>

        {timeDisplay}
      </>
    )
  }

  if (dayDiff === -1) {
    return (
      <>
        <Paragraph size='$5' fontWeight='$5'>
          Yesterday
        </Paragraph>

        {timeDisplay}
      </>
    )
  }

  return (
    <>
      <Paragraph size='$5' fontWeight='$5'>
        {format(
          startDate,
          yearDiff === 0 ? `EEEE, MMMM do` : `EEEE, MMMM do yyyy`,
          formatOptions
        )}
      </Paragraph>

      {timeDisplay}
    </>
  )
}

const DifferentDays = ({
  startDate,
  endDate,
  formatOptions,
}: {
  startDate: Date
  endDate: Date
  formatOptions: { locale: typeof enUS | typeof enGB; timeZone: string }
}) => {
  const nowToYearDiff = useMemo(() => {
    return differenceInCalendarYears(
      utcToZonedTime(new Date(), formatOptions.timeZone),
      startDate
    )
  }, [startDate, formatOptions.timeZone])

  const monthDiff = useMemo(() => {
    return differenceInCalendarMonths(endDate, startDate)
  }, [startDate, endDate])

  if (monthDiff === 0) {
    return (
      <>
        <Paragraph size='$5' fontWeight='$5'>
          {format(
            startDate,
            nowToYearDiff === 0 ? `EEEE, MMMM do` : `EEE, MMMM do`,
            formatOptions
          )}
          -
          {format(
            endDate,
            nowToYearDiff === 0 ? `do` : `do yyyy`,
            formatOptions
          )}
        </Paragraph>

        <Paragraph size='$4' fontWeight='$5' color='$grey8'>
          {format(startDate, `h:mma`, formatOptions)}{' '}
          <Text size='$4' color='$grey7' fontWeight='$3'>
            to
          </Text>{' '}
          {format(endDate, `h:mma`, formatOptions)}{' '}
          <Text size='$4' color='$grey7' fontWeight='$3'>
            {format(startDate, 'zzz', formatOptions)}{' '}
          </Text>
        </Paragraph>
      </>
    )
  }

  return (
    <>
      <Paragraph size='$5' fontWeight='$5'>
        {format(startDate, `MMM do`, formatOptions)}{' '}
        <Text size='$5' color='$grey7'>
          to
        </Text>{' '}
        {format(
          endDate,
          nowToYearDiff === 0 ? `MMM do` : `MMM do yyyy`,
          formatOptions
        )}
      </Paragraph>

      <Paragraph size='$4' fontWeight='$5' color='$grey8'>
        {format(startDate, `h:mma`, formatOptions)}{' '}
        <Text size='$4' color='$grey7'>
          to
        </Text>{' '}
        {format(endDate, `h:mma`, formatOptions)}{' '}
        <Text size='$4' color='$grey7'>
          {format(startDate, 'zzz', formatOptions)}{' '}
        </Text>
      </Paragraph>
    </>
  )
}
